<template>
  <v-app id="app">
    <loading-screen v-if="!state.hideLoadingScreen && !state.firstLoad"/>
    <v-main v-else>
      <v-progress-linear
        v-if="$route.name && !routeNames[$route.name].hidden_menu"
        :indeterminate="true"
        :active="state.loading"
        class="ma-0 position-absolute top-0"
        color="primary"
        height="4"
      ></v-progress-linear>
      <toolbar v-if="$route.name && !routeNames[$route.name].hidden_menu" />
      <v-container fluid justify-center fill-height class="pa-0" style="max-width: 100% !important;">
        <v-layout justify-center align-start>
          <v-fade-transition mode="out-in">
            <router-view :key="$route.fullPath"/>
          </v-fade-transition>
        </v-layout>
      </v-container>
      <template v-if="state.showFooter">
        <v-footer app padless color="white" class="justify-center">
          <v-layout 
            v-if="urlCookiesPolicy || urlPrivacyPolicy" 
            justify-center
            class="pb-2 px-2 py-4 text-center caption flex-none full-width" 
          >
            <span 
              v-if="urlCookiesPolicy" 
              class="clickable underline" 
              @click="routing.methods.openInNewTab({ route: urlCookiesPolicy, external: true })" 
              v-text="$t('main.cookies_policy')" 
            />
            <v-divider v-if="urlCookiesPolicy && urlPrivacyPolicy"
              class="mx-2"
              vertical
            ></v-divider>
            <span 
              v-if="urlPrivacyPolicy" 
              class="clickable underline" 
              @click="routing.methods.openInNewTab({ route: urlPrivacyPolicy, external: true })" 
              v-text="$t('main.privacy_policy')"
            />
          </v-layout>
          <div class="dark--text px-2 pb-4 text-center caption">
            v{{pjson.version}} Copyright © easygoband World S.L. {{moment().format('YYYY')}}
          </div>
        </v-footer>
      </template>
    </v-main>
    <v-snackbar
      v-model="state.message.show"
      bottom
      :color="state.message.color"
      :timeout="state.message.timeout"
    >
      {{ state.message.text }}
      <v-spacer/>
      <v-btn
        text
        class="mx-0 white--text"
        v-if="state.message.redirect"
        @click="$router.push({ name: state.message.redirect.routeName })">
        {{ state.message.redirect.text }}
      </v-btn>
      <v-btn
        text
        class="mx-0 white--text"
        v-if="state.message.timeout === 0"
        @click="state.message.show = false">
        {{$t('actions.dismiss')}}
      </v-btn>
    </v-snackbar>
    <side-menu />
  </v-app>
</template>

<script>
import routing from '@/mixins/Routing.js'
import { FONT_FAMILY_NAME } from '@/config/environment'
import { URL_COOKIES_POLICY, URL_PRIVACY_POLICY } from '@/config/environment'
import { themeByColor, hexToCSSVariable } from '@/lib/helpers/ColorUtils'
import pjson from '@/../package.json'
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'
import routeNames from '@/router/routes'
import store from '@/store'


export default {
  name: 'App',
  data () {
    return {
      routing,
      moment,
      pjson,
      routeNames,
      urlCookiesPolicy: URL_COOKIES_POLICY,
      urlPrivacyPolicy: URL_PRIVACY_POLICY,
      theme: themeByColor(this.$vuetify.theme.themes.light.accent.darken1),
    }
  },
  components: {
    LoadingScreen: () => import('@/components/app-components/LoadingScreen.vue'),
    Toolbar: () => import('@/components/app-components/Toolbar.vue'),
    SideMenu: () => import('@/components/view-components/SideMenu.vue'),
  },
  computed: {
    ...mapState(['state', 'auth']),
    ...mapGetters({ 
      authStatus: 'auth/authStatus',
      authToken: 'auth/authToken',
    }),
    configuration () {
      return this.$store.state.service.configuration
    },
    settings () {
      return this.$store.state.me.settings
    },
  },
  watch: {
    configuration (newValue, oldValue) {
      if (newValue) {
        let theme = this.$vuetify.theme.themes.light
        if (newValue.customization.primaryColor !== undefined) {
          theme.primary = {
            base: newValue.customization.primaryColor.base,
            lighten1: newValue.customization.primaryColor.light1,
            lighten2: newValue.customization.primaryColor.light2,
            darken1: newValue.customization.primaryColor.dark1,
            darken2: newValue.customization.primaryColor.dark2,
          }
        }
        if (newValue.customization.secondaryColor !== undefined) {
          theme.secondary = {
            base: newValue.customization.secondaryColor.base,
            lighten1: newValue.customization.secondaryColor.light1,
            lighten2: newValue.customization.secondaryColor.light2,
            darken1: newValue.customization.secondaryColor.dark1,
            darken2: newValue.customization.secondaryColor.dark2,
          }
        }
        if (newValue.customization.gradient !== undefined) {
          theme.accent = {
            lighten1: newValue.customization.gradient.startColor,
            darken1: newValue.customization.gradient.endColor,
          }
        }
        this.$vuetify.theme.themes.light = theme
        const metaThemeColor = document.querySelector('meta[name=theme-color]')
        metaThemeColor.setAttribute('content', newValue.customization.primaryColor.light1)

        if (newValue.customization.appTitle !== null) {
          let languageCode = this.$i18n.locale
          let appTitle = newValue.customization.appTitle.filter(
            (name) => name.languageCode === languageCode
          )[0]
          if(appTitle == null) {
            appTitle = newValue.customization.appTitle[0]
          }
          document.title = appTitle.value
          return appTitle.value
        }else{
          document.title = 'Goguest'
        }
      }
    },
    settings (newValue, oldValue) {
      if (newValue.languageCode !== undefined && newValue.languageCode !== null) {
        this.$i18n.locale = newValue.languageCode
        this.$vuetify.lang.current = newValue.languageCode
      }
    },
    group () {
      this.drawer = false
    },
  },
  created () {
    if (store.state.state.inactivityTimeout !== null) {
      let time = store.state.state.inactivityTimeout

      setInterval(() => {
        if (this.authToken !== null) {
          if (store.state.idleVue.isIdle) {
            time -= 1
          } else {
            time = store.state.state.inactivityTimeout
          }

          if (time < 1) {
            store.dispatch('auth/signOut')
          }
        }
      }, 1000)
    }
    this.$store.dispatch('service/getConfiguration', { authenticated: this.authStatus }).then(() => {
      const primaryColor = this.$vuetify.theme.currentTheme.primary
      document.documentElement.style.setProperty('--v-primary-lighten1-rgb', hexToCSSVariable(primaryColor.lighten1))
      document.documentElement.style.setProperty('--v-primary-darken1-rgb', hexToCSSVariable(primaryColor.darken1))
    }).catch(() => {})
    this.$store.dispatch('pages/getDashboardItems').catch(() => {})
  },
  mounted () {
    if ('fonts' in document) {
      document.documentElement.style.setProperty('--font-family-name', FONT_FAMILY_NAME || 'Urbanist')
    }
  },
}
</script>

<style lang="scss">
@import '../public/fonts/Avenir Next/stylesheet';

// APP TEXT STYLES

.H1 {
  font-family: 'Urbanist' !important;
  font-size: 22px !important;
  font-style: normal !important;
  font-weight: 700 !important;
  line-height: normal !important;
  letter-spacing: 0.25px !important;
}

.H2 {
  font-family: var(--font-family-name), 'Urbanist', sans-serif !important;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.25px;
}

.H3 {
  font-family: var(--font-family-name), 'Urbanist', sans-serif !important;
  font-size: 18px !important;
  font-style: normal !important;
  font-weight: 700 !important;
  line-height: normal !important;
  letter-spacing: 0.25px !important;
}

.H4 {
  font-family: var(--font-family-name), 'Urbanist', sans-serif !important;
  font-size: 16px !important;
  font-style: normal !important;
  font-weight: 700 !important;
  letter-spacing: 0.25px !important;
}

.H5 {
  font-family: var(--font-family-name), 'Urbanist', sans-serif !important;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 106.7%;
  letter-spacing: 0.25px;
}

.H6 {
  font-family: var(--font-family-name), 'Mukta Mahee', sans-serif !important;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  color: #1A1D44;
}

.H8 {
  font-family: 'Urbanist';
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.H10 {
  font-family: 'Urbanist';
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.3px;
}

.app-body-2 {
  font-family: 'Mukta';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.body-cards__A {
  font-family: 'Mukta Mahee';
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.interactive-chip {
  font-family: 'Mukta Mahee';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.status-chip {
  text-align: center;
  font-family: 'Mukta Mahee';
  font-size: 13px !important;
  font-style: normal;
  font-weight: 400;
  line-height: 20px !important;
  letter-spacing: 0.7px;
  padding: 2px 8px 2px 8px !important;
}

.pricing-number-text {
  font-family: 'Mukta';
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 13px;
  letter-spacing: 0.22px;
}
.pricing-currency-text {
  font-family: 'Mukta Mahee';
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 13px;
  letter-spacing: 0.22px;
}
.pricing-display-number-text {
  font-family: 'Mukta';
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 10px !important;
  letter-spacing: 0.22px;
}


// VUETIFY BUTTONS

.v-btn.floating {
  &:not(.v-btn--round) {
    border-radius: 50px !important;
  }
  height: 40px !important;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.15) !important;
  text-transform: none !important;
}
.v-btn.outlined {
  border: 1.2px solid inherit !important;
}
.white-overlay {
  .v-overlay__scrim {
    background-color: rgba(255, 255, 255, 0.80) !important;
  }
}
.no-uppercase.v-btn {
  text-transform: none !important;
}


// PAGE STYLES

.featured-section {
  padding: 10px 0 10px 0 !important;
}

.light-background {
  .icon__background {
    background-color: rgba(var(--v-primary-lighten1-rgb), 0.2) !important;
  }
}

.icon__background {
  display: flex;
  place-content: center;
  border-radius: 50px;
  padding: 5px;
  width: 34px;
  height: 34px;
  background-color: rgba(var(--v-primary-lighten1-rgb), 0.1);

  .v-image {
    height: 24px;
    width: 24px;
  }
}

.page-chip {
  height: 25px !important;
  color: #FFF;
  font-family: Roboto;
  font-size: 14px !important;
  font-style: normal;
  font-weight: 500;
  letter-spacing: 0.4px;
  .v-chip__content {
    height: 25px !important;
  }
}

.flex-button-container {
  .layout {
    flex-wrap: nowrap;
  }
  .flex:not(:last-child) {
    padding-right: 10px;
    flex-grow: 1 !important;
    flex-basis: 50%;

    .v-btn {
      width: 100% !important;
    }
  }
}

.colored-icons {
  .attendees_badge {
    background-color: var(--v-primary-darken1);
  }
}

.attendees_badge {
  border-radius: 50px;
  margin-left: 3px;
  width: 15px;
  height: 15px;
  background-color: var(--v-primary-lighten1);
  color: #FFF;
  text-align: center;
  font-family: 'Mukta Mahee';
  font-size: 10.435px;
  font-style: normal;
  font-weight: 500;
  line-height: 10.435px;
  letter-spacing: 0.261px;
  display: flex;
  justify-content: center;
  align-items: center;
  &.badge__big {
    height: 25px;
    width: 25px;
    color: #FFF;
    text-align: center;
    font-family: 'Mukta Mahee';
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: 0.4px;
  }
}

.gap-1 {
  gap: 4px;
}

.font-weight-700 {
  font-weight: 700 !important;
}

.no-wrap {
  flex-wrap: nowrap !important;
}

// CUSTOM COLORS

*.light-background {
  background: rgba(var(--v-primary-lighten1-rgb), 0.1) !important;
}

.light-background {
  .icon__background {
    background-color: rgba(var(--v-primary-lighten1-rgb), 0.2) !important;
  }
}

.text-color__dark {
  color: var(--v-dark-text-base) !important;
}

// APP WIDTH

.section-max-width {
  overflow-y: auto;
  max-width: 600px !important;
}

.section-padding {
  @media (min-width: 400px) {
    padding: 0 30px !important;
  }
  padding: 0 20px !important;
}

.featured-page__container {
  overflow-x: auto;
  display: flex;
  flex-direction: row;
  max-width: 100%;
  .dashboard-item {
    flex: none;
  }
  @media (min-width: 400px) {
    padding-left: 30px !important;
  }
  padding-left: 20px !important;
}

.margin-on-children {
  margin-left: -30px;
  margin-right: -30px;
  > .layout.row {
    margin-left: 30px !important;
    margin-right: 30px !important;
  }
}
.margin-on-last-child:last-child {
  margin-bottom: 60px !important;
}
.app-scroll-section.section-margins {
  margin-left: -30px;
  margin-right: -30px;
  padding-left: 30px;
  padding-right: 30px;
  max-width: calc(100% + 58px);
}

#app.v-application {
  font-family: var(--font-family-name), 'Roboto', sans-serif !important;
  .display-4,
  .display-3,
  .display-2,
  .display-1,
  .headline,
  .title,
  .subheading,
  .body-2,
  .body-1,
  .caption {
    font-family: var(--font-family-name), 'Roboto', sans-serif !important;
  }
}
.v-alert--text:before {
  opacity: .0 !important;
}

html, body {
  overflow: hidden !important;
  height: 100%;
  min-height: -webkit-stretch-content;
}
#app {
  width: 100% !important;
  position:fixed;
  top:0;
  bottom:0;
  overflow:hidden;
}

.v-main {
  max-height: 100%;
  .v-main__wrap {
    overflow: auto;
    position: relative;
  }
}
* {
  word-break: break-word;
}
.v-application .v-application--wrap {
  min-height: 100% !important;
}
.overflow-y-hidden {
  height: 100%;
}
.overflow-visible {
  overflow: visible !important;
}
.fit-content {
  min-height: fit-content !important;
}
.flex-none {
  flex: none !important;
}
.flex-item {
  flex: 1 auto !important;
}
.wrap-reverse {
  flex-wrap: wrap-reverse !important;
}
.hidden {
  visibility: hidden;
}
.margin-auto {
  margin: auto !important;
}
.full-height {
  height: 100%;
}
.login-background {
  background-size: cover;
  background-image: linear-gradient(to right top, var(--v-accent-lighten1), var(--v-accent-darken1));
  background-blend-mode: overlay;
}
.max-width-400 {
  padding: 40px 20px 40px 20px !important;
  max-width: 440px !important;
}
.full-width {
  width: 100%;
}
.pre-wrap {
  white-space: pre-wrap;
}
.theme--light.v-input--is-disabled input {
  color: rgba(0,0,0,.87);
}
.required label::after{
  content: '*';
}
.app-section-width {
  max-width: 600px !important;
}
.menu-section-width {
  max-width: 700px !important;
}
.section-width {
  max-width: 800px !important;
}
.app-scroll-section {
  height: 100% !important;
  overflow-y: auto !important;
  overflow-x: hidden;
}
.v-btn:not(.v-btn--round) {
  border-radius: 10px !important;
  box-shadow: none;
}
.v-btn.primary-gradient {
  border: double 1px transparent;
  background: linear-gradient(white, white), linear-gradient(225deg, var(--v-primary-lighten1), var(--v-primary-darken1));
  background-origin: border-box;
  background-clip: content-box, border-box;
  padding: 0 !important;
}
.v-card.border-gradient:hover {
  border: double 2px transparent;
  background: linear-gradient(white, white), linear-gradient(225deg, var(--v-primary-lighten1), var(--v-primary-lighten1));
  background-origin: border-box;
  background-clip: content-box, border-box;
  padding: 0 !important;
}
.clickable {
  cursor: pointer;
}
.underline {
  text-decoration-line: underline;
}
.activity-title {
  white-space: nowrap;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-flex;
  color: white;
  background: rgba(black, 0.5);
  padding: 2px 10px !important;
}
.activity-subtitle {
  white-space: inherit;
  display: block;
  line-height: 17px;
}
.activity-header {
  background-size: cover;
  background-position: center center;
  height: 135px !important;
}
.activity-button {
  border-radius: 4px;
  background-size: cover;
  background-position: center center;
  height: 135px !important;
}
.show-spaces {
  white-space: pre;
}
.line-through {
  text-decoration: line-through;
}

// Space in column or row
.all-gap-10 {
  row-gap: 10px;
  column-gap: 10px;
}

// Button
.button-level-3 {
  border: thin solid #DCDCDC !important;
  color: #003333 !important;
}

.details-header {
  background-size: cover;
  background-position: center center;
  height: 135px !important;
  .details-title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    display: inline-flex;
    color: white;
    background: rgba(black, 0.5);
  }
}
.details-title {
  color: grey;
  font-size: 24px;
}
.details-data {
  color: grey;
  font-size: 18px;
}
.details-data-bold {
  font-weight: bold;
  color: grey;
  font-size: 18px;
}
</style>
